import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { crmApi } from '../api/crm-api';
import type { CreateLeadRequest, Lead, LeadStatus } from './types';

export const crmKeys = {
  all: ['crm'] as const,
  leads: () => [...crmKeys.all, 'leads'] as const,
};

export function useLeadsQuery() {
  return useQuery({
    queryKey: crmKeys.leads(),
    queryFn: () => crmApi.list(),
    staleTime: 30_000,
  });
}

export function useCreateLeadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLeadRequest) => crmApi.create(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: crmKeys.leads() }),
  });
}

interface PatchStatusVars {
  id: string;
  status: LeadStatus;
}

export function useChangeLeadStatusMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: PatchStatusVars) => crmApi.patchStatus(id, status),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: crmKeys.leads() });
      const previous = qc.getQueryData<Lead[]>(crmKeys.leads());
      qc.setQueryData<Lead[]>(crmKeys.leads(), (old) =>
        old?.map((l) => (l.id === id ? { ...l, status } : l)) ?? old,
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(crmKeys.leads(), ctx.previous);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: crmKeys.leads() }),
  });
}

export function useUpdateLeadNotesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string | null }) =>
      crmApi.patchNotes(id, notes),
    onSuccess: (lead) => {
      qc.setQueryData<Lead[]>(crmKeys.leads(), (old) =>
        old?.map((l) => (l.id === lead.id ? lead : l)) ?? old,
      );
    },
  });
}

export function useUpdateLeadTitleMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, title }: { id: string; title: string }) => crmApi.patchTitle(id, title),
    onSuccess: (lead) => {
      qc.setQueryData<Lead[]>(crmKeys.leads(), (old) =>
        old?.map((l) => (l.id === lead.id ? lead : l)) ?? old,
      );
    },
  });
}
